import { Building2, User, UserPlus, Eye, EyeOff, CheckCircle, ArrowLeft, Store, ShoppingBag } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

type UserType = 'minorista' | 'mayorista' | null;

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Determinar tipo inicial desde URL
  const getInitialType = (): UserType => {
    const type = searchParams.get('type');
    if (type === 'mayorista') return 'mayorista';
    if (type === 'minorista') return 'minorista';
    return null;
  };

  const [userType, setUserType] = useState<UserType>(getInitialType);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [departamentos, setDepartamentos] = useState<string[]>([]);
  const [municipios, setMunicipios] = useState<string[]>([]);
  const [selectedDepartamento, setSelectedDepartamento] = useState<string>('');
  const [municipioSeleccionado, setMunicipioSeleccionado] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [success, setSuccess] = useState(false);

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (userType === 'mayorista') {
      fetchDepartamentos();
    }
  }, [userType]);

  useEffect(() => {
    if (selectedDepartamento) {
      fetchMunicipios(selectedDepartamento);
    }
  }, [selectedDepartamento]);

  useEffect(() => {
    if (confirmPassword) {
      setPasswordMatch(password === confirmPassword);
    } else {
      setPasswordMatch(true);
    }
  }, [password, confirmPassword]);

  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/\d/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;

    setPasswordStrength(strength);
  }, [password]);

  const fetchDepartamentos = async () => {
    try {
      const { data, error } = await supabase.rpc('get_departamentos');
      if (error) throw error;
      setDepartamentos(data || []);
    } catch (err) {
      console.error('Error al cargar departamentos:', err);
    }
  };

  const fetchMunicipios = async (departamento: string) => {
    try {
      const { data, error } = await supabase.rpc('get_municipios', {
        p_departamento: departamento
      });
      if (error) throw error;
      setMunicipios(data || []);
      setMunicipioSeleccionado('');
    } catch (err) {
      console.error('Error al cargar municipios:', err);
    }
  };

  const resetForm = () => {
    setError(null);
    setPassword('');
    setConfirmPassword('');
    setPasswordStrength(0);
    setPasswordMatch(true);
    setSelectedDepartamento('');
    setMunicipios([]);
    setMunicipioSeleccionado('');
    setName('');
    setCompany('');
    setPhone('');
    setEmail('');
    setSuccess(false);
  };

  const handleTypeChange = (type: UserType) => {
    resetForm();
    setUserType(type);
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 1) return 'bg-red-500';
    if (passwordStrength <= 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (!password) return '';
    if (passwordStrength <= 1) return 'Débil';
    if (passwordStrength <= 3) return 'Media';
    return 'Fuerte';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    // Validaciones según tipo
    if (userType === 'mayorista') {
      if (!name || !company || !phone || !selectedDepartamento || !email || !password) {
        setError('Todos los campos son obligatorios');
        return;
      }
    } else {
      if (!name || !phone || !email || !password) {
        setError('Todos los campos son obligatorios');
        return;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor ingresa un email válido');
      return;
    }

    setLoading(true);

    try {
      const userData = userType === 'mayorista'
        ? {
            name,
            company,
            phone,
            department: selectedDepartamento,
            municipio: municipioSeleccionado,
            price_type: 'contado',
          }
        : {
            name,
            phone,
            price_type: 'publico',
          };

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
        },
      });

      if (signUpError) {
        throw signUpError;
      }

      if (!data?.user) {
        setError('Error al crear la cuenta. No se recibió información del usuario.');
        return;
      }

      setSuccess(true);

      // Si hay sesión, el usuario fue confirmado automáticamente
      if (data.session) {
        setTimeout(() => navigate('/'), 1500);
      } else {
        // Si no hay sesión, necesita confirmar email
        setTimeout(() => {
          navigate('/login', { state: { message: 'Cuenta creada exitosamente. Revisa tu correo para confirmar tu cuenta.' } });
        }, 2000);
      }
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch')) {
          setError('No se pudo conectar con el servidor. Verifica tu conexión a internet.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Error al crear la cuenta');
      }
    } finally {
      setLoading(false);
    }
  };

  // Pantalla de selección de tipo
  if (!userType) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-3xl w-full space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Crear cuenta en Disolgua
            </h1>
            <p className="text-gray-600">
              Selecciona el tipo de cuenta que mejor se adapte a ti
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {/* Opción Minorista */}
            <button
              onClick={() => handleTypeChange('minorista')}
              className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-500 text-left"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-green-100 p-4 rounded-full group-hover:bg-green-200 transition-colors">
                  <ShoppingBag className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Cliente Minorista</h2>
                  <span className="text-sm text-green-600 font-medium">Compras personales</span>
                </div>
              </div>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  Registro rápido y sencillo
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  Precios de público general
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  Ideal para regalos y uso personal
                </li>
              </ul>
              <div className="mt-6 text-green-600 font-medium group-hover:text-green-700 flex items-center gap-2">
                Seleccionar
                <ArrowLeft className="h-4 w-4 rotate-180 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>

            {/* Opción Mayorista */}
            <button
              onClick={() => handleTypeChange('mayorista')}
              className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-blue-500 text-left relative overflow-hidden"
            >
              <div className="absolute top-4 right-4 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full">
                MEJORES PRECIOS
              </div>
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-blue-100 p-4 rounded-full group-hover:bg-blue-200 transition-colors">
                  <Store className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Cliente Mayorista</h2>
                  <span className="text-sm text-blue-600 font-medium">Negocios y empresas</span>
                </div>
              </div>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  Precios especiales de mayoreo
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  Acceso a mejores precios por volumen
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  Requiere datos de negocio
                </li>
              </ul>
              <div className="mt-6 text-blue-600 font-medium group-hover:text-blue-700 flex items-center gap-2">
                Seleccionar
                <ArrowLeft className="h-4 w-4 rotate-180 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          </div>

          <p className="text-center text-sm text-gray-600 mt-8">
            ¿Ya tienes una cuenta?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 underline">
              Inicia sesión aquí
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Pantalla de éxito
  if (success) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Registro exitoso!</h2>
          <p className="text-gray-600">
            Tu cuenta ha sido creada correctamente. Redirigiendo...
          </p>
        </div>
      </div>
    );
  }

  // Formulario de registro
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-2xl w-full space-y-8 bg-white p-8 rounded-2xl shadow-lg">
        {/* Header con botón volver */}
        <div>
          <button
            onClick={() => handleTypeChange(null)}
            className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Cambiar tipo de cuenta
          </button>

          <div className="text-center">
            <div className="flex justify-center">
              <div className={`p-4 rounded-full ${userType === 'mayorista' ? 'bg-blue-100' : 'bg-green-100'}`}>
                {userType === 'mayorista'
                  ? <Building2 className="h-12 w-12 text-blue-600" />
                  : <User className="h-12 w-12 text-green-600" />
                }
              </div>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-gray-900">
              Registro {userType === 'mayorista' ? 'Mayorista' : 'Minorista'}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {userType === 'mayorista'
                ? 'Accede a precios especiales para tu negocio'
                : 'Crea tu cuenta personal para comprar'
              }
            </p>
          </div>
        </div>

        <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className={`grid gap-5 ${userType === 'mayorista' ? 'md:grid-cols-2' : ''}`}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Nombre completo <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Tu nombre completo"
              />
            </div>

            {userType === 'mayorista' && (
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del negocio <span className="text-red-500">*</span>
                </label>
                <input
                  id="company"
                  type="text"
                  required
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Nombre de tu empresa"
                />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono <span className="text-red-500">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="2222-2222"
            />
          </div>

          {userType === 'mayorista' && (
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="departamento" className="block text-sm font-medium text-gray-700 mb-1">
                  Departamento <span className="text-red-500">*</span>
                </label>
                <select
                  id="departamento"
                  required
                  value={selectedDepartamento}
                  onChange={(e) => setSelectedDepartamento(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                >
                  <option value="">Seleccione</option>
                  {departamentos.map(dep => (
                    <option key={dep} value={dep}>{dep}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="municipio" className="block text-sm font-medium text-gray-700 mb-1">
                  Municipio
                </label>
                <select
                  id="municipio"
                  value={municipioSeleccionado}
                  onChange={(e) => setMunicipioSeleccionado(e.target.value)}
                  disabled={!selectedDepartamento}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white disabled:bg-gray-100"
                >
                  <option value="">Seleccione</option>
                  {municipios.map(mun => (
                    <option key={mun} value={mun}>{mun}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Correo electrónico <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="tu@email.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all pr-12"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {password && (
              <div className="mt-2">
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getPasswordStrengthColor()} transition-all`}
                    style={{ width: `${(passwordStrength / 5) * 100}%` }}
                  />
                </div>
                <p className="text-xs mt-1 text-gray-600">
                  Fortaleza: <span className="font-medium">{getPasswordStrengthText()}</span>
                </p>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar contraseña <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all pr-12 ${
                  confirmPassword && !passwordMatch
                    ? 'border-red-500'
                    : confirmPassword && passwordMatch
                      ? 'border-green-500'
                      : 'border-gray-300'
                }`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
              {confirmPassword && passwordMatch && (
                <CheckCircle className="absolute right-12 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
              )}
            </div>
            {confirmPassword && !passwordMatch && (
              <p className="text-xs mt-1 text-red-500">Las contraseñas no coinciden</p>
            )}
          </div>

          <div className="flex items-center">
            <input
              id="terms"
              type="checkbox"
              required
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="terms" className="ml-2 block text-sm text-gray-700">
              Acepto los{' '}
              <Link to="/terms" className="text-blue-600 hover:text-blue-500">
                términos y condiciones
              </Link>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || (confirmPassword !== '' && !passwordMatch)}
            className={`w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
              userType === 'mayorista'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
            }`}
          >
            <span className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creando cuenta...
                </>
              ) : (
                `Crear cuenta ${userType === 'mayorista' ? 'mayorista' : 'minorista'}`
              )}
            </span>
          </button>
        </form>

        <p className="text-center text-sm text-gray-600">
          ¿Ya tienes una cuenta?{' '}
          <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 underline">
            Inicia sesión aquí
          </Link>
        </p>
      </div>
    </div>
  );
}
